import type { jsPDF } from "jspdf";
import { formatMoney } from "./format";

/** Image prête pour un PDF : contenu en data URL et dimensions naturelles (pour garder les proportions). */
export interface PdfImage {
  data: string;
  format: "PNG" | "JPEG" | "WEBP";
  width: number;
  height: number;
  /** Image déjà décodée (recadrage sur canevas). */
  element: HTMLImageElement;
}

const cache = new Map<string, Promise<PdfImage | null>>();

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function decode(data: string): Promise<{ width: number; height: number; element: HTMLImageElement }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight, element: img });
    img.onerror = () => reject(new Error("image illisible"));
    img.src = data;
  });
}

/**
 * Charge une image (URL du serveur ou data URL) pour l'insérer dans un PDF. Une image absente ou
 * illisible donne `null` : le document s'imprime quand même, sans elle.
 */
export function loadPdfImage(url: string | null | undefined): Promise<PdfImage | null> {
  if (!url) return Promise.resolve(null);
  let pending = cache.get(url);
  if (!pending) {
    pending = (async () => {
      try {
        const data = url.startsWith("data:") ? url : await readAsDataUrl(await (await fetch(url)).blob());
        const mime = /^data:image\/(png|jpe?g|webp)/i.exec(data)?.[1]?.toLowerCase();
        if (!mime) return null;
        const format = mime === "png" ? "PNG" : mime === "webp" ? "WEBP" : "JPEG";
        return { data, format, ...(await decode(data)) };
      } catch {
        return null;
      }
    })();
    cache.set(url, pending);
  }
  return pending;
}

/** Dessine l'image entière dans le cadre (x, y, w, h), centrée, sans la déformer. */
export function drawImageContain(doc: jsPDF, image: PdfImage, x: number, y: number, w: number, h: number): void {
  const ratio = Math.min(w / image.width, h / image.height);
  const iw = image.width * ratio;
  const ih = image.height * ratio;
  try {
    doc.addImage(image.data, image.format, x + (w - iw) / 2, y + (h - ih) / 2, iw, ih);
  } catch {
    /* format refusé par jsPDF : le cadre reste vide */
  }
}

/** Remplit le cadre avec l'image (recadrée au centre), comme une photo d'identité. */
export function drawImageCover(doc: jsPDF, image: PdfImage, x: number, y: number, w: number, h: number): void {
  // jsPDF ne recadre pas : on recadre sur un canevas avant d'insérer.
  try {
    const canvas = document.createElement("canvas");
    const pxPerMm = 11.3; // ≈ 290 points par pouce : net à l'impression
    canvas.width = Math.round(w * pxPerMm);
    canvas.height = Math.round(h * pxPerMm);
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      drawImageContain(doc, image, x, y, w, h);
      return;
    }
    const ratio = Math.max(canvas.width / image.width, canvas.height / image.height);
    const sw = canvas.width / ratio;
    const sh = canvas.height / ratio;
    ctx.drawImage(image.element, (image.width - sw) / 2, (image.height - sh) / 2, sw, sh, 0, 0, canvas.width, canvas.height);
    doc.addImage(canvas.toDataURL("image/jpeg", 0.9), "JPEG", x, y, w, h);
  } catch {
    drawImageContain(doc, image, x, y, w, h);
  }
}

/** Montant dans la devise de l'école, avec des espaces simples (les espaces insécables s'impriment mal). */
export const pdfMoney = (amount: number): string => formatMoney(amount);

/** Date AAAA-MM-JJ (ou ISO) en JJ/MM/AAAA. */
export function pdfDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
}
